import React from "react";
import PropTypes from "prop-types";
import { Icon } from "antd";
import TvrField from "./tvr-field";

/**
 * One group of the Credit-TVR form: a heading plus a responsive grid of fields.
 *
 * The whole TVR now has a SINGLE global edit toggle (see UpdatedTvrSection) —
 * there is no per-subsection pencil, submit or cancel. When `editing` is true
 * every field in the section becomes editable; when false, the section is
 * read-only. The final Submit lives at the bottom of the entire form.
 */
const TvrSubsection = ({
    section,
    editing,
    saving,
    valueFor,
    optionsFor,
    editedFor,
    onChangeField,
    suggestionFor,
    shouldShowSuggestion,
    suggestionBusy,
    onAcceptSuggestion,
    onRejectSuggestion,
    footer
}) => {
    const grid = (
        <div className={"cc-tvr-grid"}>
            {section.fields.map(field => (
                <React.Fragment key={field.key}>
                    {/* A zero-height full-width cell: it consumes the rest of the
                        current grid row, so the field after it always starts a
                        fresh line (see `rowStart` in tvr-config). */}
                    {field.rowStart ? (
                        <div className={"cc-tvr-rowbreak"} aria-hidden />
                    ) : null}
                    <TvrField
                        label={field.label}
                        type={field.type}
                        options={
                            optionsFor ? optionsFor(field) : field.options
                        }
                        columns={field.columns}
                        min={field.min}
                        max={field.max}
                        suffix={field.suffix}
                        wide={field.wide}
                        value={valueFor(field)}
                        edited={editedFor(field)}
                        editing={editing}
                        saving={saving}
                        onChange={value => onChangeField(field, value)}
                        suggestion={
                            suggestionFor ? suggestionFor(field) : null
                        }
                        showSuggestion={
                            shouldShowSuggestion
                                ? shouldShowSuggestion(field)
                                : false
                        }
                        suggestionBusy={
                            suggestionBusy === field.path ||
                            suggestionBusy === "all"
                        }
                        onAcceptSuggestion={
                            onAcceptSuggestion
                                ? () => onAcceptSuggestion(field)
                                : undefined
                        }
                        onRejectSuggestion={
                            onRejectSuggestion
                                ? () => onRejectSuggestion(field)
                                : undefined
                        }
                    />
                </React.Fragment>
            ))}
        </div>
    );
    {/* Extra content that belongs to this section but is not a form field —
        e.g. Business Overview renders the shared Brand Mix table here. Sections
        flagged `footerFirst` (Financial Performance) render it ABOVE the grid so
        the Limit Assessment block sits over the Remark field. */}
    const footerNode = footer ? (
        <div className={"cc-tvr-group__footer"}>{footer}</div>
    ) : null;
    return (
        <div className={"cc-tvr-group"}>
            <div className={"cc-tvr-group__heading"}>
                <Icon type={section.icon} />
                <span>{section.title}</span>
            </div>
            {section.footerFirst ? footerNode : null}
            {grid}
            {section.footerFirst ? null : footerNode}
        </div>
    );
};

TvrSubsection.propTypes = {
    section: PropTypes.object.isRequired,
    // Global edit flag from UpdatedTvrSection — the same value is passed to
    // every subsection so all fields flip in and out of edit mode together.
    editing: PropTypes.bool,
    // Global saving flag — disables inputs while the batch submit is in flight.
    saving: PropTypes.bool,
    valueFor: PropTypes.func.isRequired,
    // Resolves a field's dropdown options — static, or derived from case data
    // (see `optionsFrom` in tvr-config).
    optionsFor: PropTypes.func,
    editedFor: PropTypes.func.isRequired,
    onChangeField: PropTypes.func.isRequired,
    suggestionFor: PropTypes.func,
    shouldShowSuggestion: PropTypes.func,
    suggestionBusy: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    onAcceptSuggestion: PropTypes.func,
    onRejectSuggestion: PropTypes.func,
    // Non-field content rendered under this section's grid.
    footer: PropTypes.node
};

export default TvrSubsection;
